<template>
  <div>
    <TableWrapper
      :table-columns="thermostatsColumns"
      table-key="thermostats"
      :enable-selection="false"
      @editThermostat="goToEdit"
      @showTempsHistory="showTempsHistory"
      @editTemperature="editTemperature"
    />

    <ViewActionButtons :buttons="actionButtons" />

    <CreateEditThermostat
      v-if="openCreateEditThermostat"
      :isModalEdit="isModalEdit"
      @dialogClose="handleDialogClose"
    />

    <TemperatureLog
      v-if="openTemperatureLog && selectedThermostatId"
      :thermostatId="selectedThermostatId"
      @dialogClose="closeTempsHistory"
    />
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';

  import TableWrapper from '@/components/TableWrapper.vue';
  import ViewActionButtons from '@/components/ViewActionButtons.vue';
  import { thermostatsColumns } from '@/config';
  import { thermostatsApi } from '@/api';

  import CreateEditThermostat from './CreateEditThermostat.vue';
  import TemperatureLog from './TemperatureLog.vue';

  export default {
    name: 'ThermostatsIndex',
    components: {
      TableWrapper,
      ViewActionButtons,
      CreateEditThermostat,
      TemperatureLog,
    },
    data() {
      return {
        thermostatsColumns,
        openCreateEditThermostat: false,
        isModalEdit: false,
        selectedThermostatId: null,
        openTemperatureLog: false,
        actionButtons: [
          {
            text: this.$t('thermostats.create.createThermostat'),
            type: 'primary',
            permissions: ['thermostatsManage'],
            action: () => {
              this.openCreateEditThermostat = true;
            },
          },
        ],
      };
    },
    computed: {
      ...mapGetters(['tableFilters']),
    },
    created() {
      this.parseQueryStringStatus(this.$route.query);
      this.tableRows();
    },
    beforeRouteEnter(to, from, next) {
      next((vm) => {
        if (to.params.thermostatId) vm.editThermostat();
      });
    },
    beforeRouteUpdate(to, from, next) {
      this.parseQueryStringStatus(to.query);
      this.tableRows();
      next();
    },
    methods: {
      ...mapActions([
        'updateTableRows',
        'updateTableCount',
        'parseQueryStringStatus',
      ]),
      async tableRows() {
        try {
          const thermostatsIndexResponse = await thermostatsApi.index(
            this.tableFilters('thermostats')
          );
          const thermostatsCountResponse = await thermostatsApi.count();
          this.updateTableRows({
            tableKey: 'thermostats',
            rows: thermostatsIndexResponse.data,
          });
          this.updateTableCount({
            tableKey: 'thermostats',
            count: thermostatsCountResponse.data.count,
          });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      editThermostat() {
        this.isModalEdit = true;
        this.openCreateEditThermostat = true;
      },
      goToEdit(row) {
        this.$router.push({
          name: 'thermostatsEdit',
          params: { thermostatId: row.id },
        });
      },
      showTempsHistory(row) {
        this.selectedThermostatId = row.id;
        this.openTemperatureLog = true;
      },
      closeTempsHistory() {
        this.selectedThermostatId = null;
        this.openTemperatureLog = false;
      },
      editTemperature(row) {
        this.$prompt(
          this.$t('thermostats.temperature.message'),
          this.$t('thermostats.temperature.title'),
          {
            confirmButtonText: this.$t('global.confirm'),
            cancelButtonText: this.$t('global.cancel'),
            inputType: 'number',
            inputValidator: this.validateTemperature,
          }
        )
          .then(({ value }) =>
            thermostatsApi.createTemperature(row.id, {
              value: parseFloat(value),
            })
          )
          .then(() => {
            this.$notify({
              showClose: true,
              type: 'success',
              message: this.$t('thermostats.update.success'),
            });
            this.tableRows();
          })
          .catch((error) => {
            if (error.response) {
              this.$notify({
                showClose: true,
                type: 'error',
                message: error.response.data.error.message,
              });
            }
          });
      },
      handleDialogClose(status) {
        this.openCreateEditThermostat = false;
        if (status.success) this.tableRows();
        if (this.isModalEdit) {
          this.isModalEdit = false;
          this.$router.push({ name: 'thermostats' });
        }
      },
      validateTemperature(value) {
        return !Number.isNaN(value) && !value.includes(',');
      },
    },
  };
</script>
